import type {
  PlanListEntry,
  PriceWithheldReason,
  PricePointsResponse,
} from '@rheora/enrollment-contract';
import { PROBLEM_TYPES } from '@rheora/enrollment-contract';

/**
 * WHAT WE ARE ENTITLED TO SAY ABOUT A PLAN'S PRICE — the storefront's side of
 * Pricing's §25.475 publication decision.
 *
 * ── THE DEFECT THIS CLOSES ─────────────────────────────────────────────────
 * An agent composed an offer for 4Change Maxx Saver Value 12. The customer opened
 * the emailed link and the card read:
 *
 *     4Change Maxx Saver Value 12
 *     Provider: 4Change Energy
 *     Price unavailable            ← this
 *     12-month term · 6% renewable · Cancel fee $295.00
 *
 * The agent had quoted a price the customer could not see, on a card that read as
 * a broken page. Pricing was RIGHT: the plan advertises a bill credit that the
 * EFL average-price calculation does not model, and publishing the uncredited
 * figure would have overstated the price 5.7× (15.15 ¢/kWh against a true 2.65 at
 * 1,000 kWh) — §25.475(c)(1)(A) forbids exactly that. 8 of the live 44-tariff
 * catalogue refuse for the same reason.
 *
 * What was wrong was the TELLING. "Price unavailable" is what the card also says
 * when Pricing is down, so a deliberate, permanent, perfectly explainable decision
 * was delivered in the words of an unexplained transient fault — and the customer
 * was given no reason, no route forward, and no sign that the plan was fine.
 *
 * ── WHAT THIS MODULE IS NOT ────────────────────────────────────────────────
 * 🚫 It does NOT derive, estimate, interpolate or fall back to a price. There is
 * deliberately no path here from `rateCentsPerKwh`, from the detail page's rate
 * components, or from any other endpoint to a figure a card could headline.
 * §25.475(g)(2) governs that number; Pricing refused it for a stated reason, and
 * a number Pricing will not give is a signal to stop, not a gap to fill. The one
 * thing this module adds is the WORDS.
 *
 * Pure: no React, no I/O, no `BffError` — it takes the plain problem-type string
 * `bff-client.problemTypeOf` yields, exactly as `quote-draft-error.service` does,
 * so the classification stays here and out of the API layer.
 */

/**
 * The three things that can be true of a plan's §25.475 average price once the
 * request has settled. A discriminated union rather than "points or undefined",
 * because `WITHHELD` is an ANSWER and `undefined` was how it got mistaken for a
 * failure in the first place.
 *
 * Loading and outage are NOT members: they are query states, owned by TanStack
 * Query, and folding them in here would re-merge the two things this type exists
 * to keep apart.
 */
export type PriceDisclosure =
  | { readonly status: 'PUBLISHED'; readonly pricePoints: PricePointsResponse }
  | { readonly status: 'WITHHELD'; readonly reason: PriceWithheldReason };

/**
 * ⚠️ THE PRODUCT DECISION, IN ONE PLACE — do we shelve a plan we cannot headline
 * a price for?
 *
 * `true`: we do, with the reason on the card. Chosen, and here is why the
 * alternative loses:
 *
 *   - An offer link can consist ENTIRELY of withheld plans. Hiding them would
 *     land that customer on an empty shelf for an offer an agent legitimately
 *     sent — strictly worse than the defect being fixed, and silent at both ends.
 *   - These plans have real, publishable facts: term, renewable content, cancel
 *     fee, and an energy charge the detail page already shows under its own
 *     honest label. Blanking all of that communicates less, not more.
 *   - Withholding from the shelf is ITSELF an unratified compliance decision. It
 *     is not the conservative option; it is a different unreviewed one that also
 *     removes plans the business sells.
 *
 * ⚠️ WHAT IS STILL OPEN, AND IS NOT MINE TO RULE ON. Whether a §25.475 comparison
 * shelf may carry a card without its comparison figure at all — and whether the
 * explanatory copy below is adequate — is a disclosure judgement for
 * `audit-compliance-reviewer` / counsel, not an engineering one. It is the same
 * kind of open question `DEFAULT_PLAN_SORT` carries, and it is deliberately
 * settled by ONE constant here for the same reason: whichever way it lands is a
 * one-line change rather than a rewrite.
 *
 * TO WITHHOLD THESE PLANS FROM THE SHELF: flip to `false`. Keep the tests.
 */
export const SHELVE_PLANS_WITHOUT_PUBLISHED_PRICE = true;

/**
 * The disclosure the SHELF LIST already carried for this plan, or `undefined`
 * when it carried neither (a transient omission the card re-fetches).
 *
 * ⚠️ `pricePoints` WINS IF BOTH SOMEHOW APPEAR. They are mutually exclusive by
 * construction upstream — a refusal produces no figures — but a disclosed price
 * is the stronger claim, and preferring it means a contract drift can never
 * suppress a price we were entitled to publish.
 */
export function disclosureFromListEntry(entry: PlanListEntry): PriceDisclosure | undefined {
  if (entry.pricePoints !== undefined) {
    return { status: 'PUBLISHED', pricePoints: entry.pricePoints };
  }
  if (entry.priceWithheld !== undefined) {
    return { status: 'WITHHELD', reason: entry.priceWithheld };
  }
  return undefined;
}

/**
 * True when the BFF refused BECAUSE it does not publish an average price for this
 * plan — the 409 `price-not-published`, as opposed to any other failure.
 *
 * Branches on the stable `type` URN and never on the status: 409 is also
 * `idempotency-key-conflict`, `esiid-already-enrolled`, `offer-price-changed` and
 * more, so a status test would classify unrelated refusals as a price decision.
 */
export function isPriceNotPublished(problemType: string | undefined): boolean {
  return problemType === PROBLEM_TYPES.priceNotPublished;
}

/**
 * The i18n key explaining why no average price is shown.
 *
 * ⚠️ AN EXHAUSTIVE SWITCH ON A ONE-MEMBER UNION, ON PURPOSE. A new
 * `PriceWithheldReason` must break the build here rather than fall through to
 * generic copy — because generic copy on this surface is precisely the "Price
 * unavailable" failure being fixed, and the second reason to arrive will need its
 * own words, not the first reason's.
 */
export function withheldReasonKey(reason: PriceWithheldReason): string {
  switch (reason) {
    case 'UNMODELLED_PROMOTIONAL_TERM':
      return 'shop.card.avgPrice.withheld.promotionalTerm';
  }
}
